import React, { useRef, useState } from 'react';
import icAttachment from 'Assets/img/ic_attachment_20_basic@2x.png';
import BasicButton from 'Components/Button/BasicButton';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { createCustomerSupportChat } from 'Api/endpoints/CustomerSupportChat';
import { useError } from 'Api/hooks/useError';
import { classifyFiles } from 'Util/FileUtils';

const DOMAIN = 'MAIN';
const TYPE = 'ALL';

const InputArea = ({ refresh, startUpload }) => {
  const { defaultField } = useSelector(state => state.config);
  const [text, setText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef(null);
  const inputFileRef = useRef(null);
  const { formatMessage } = useIntl();
  const [textareaHeight, setTextareaHeight] = useState('48px');
  const { showErrorPopup } = useError();

  const sendFiles = async ({ files }) => {
    if (files?.length === 0) return;
    try {
      const maxFileSize = 200 * 1024 * 1024;
      for (let i = 0; i < files.length; i++) {
        if (files[i].size > maxFileSize) {
          showErrorPopup({
            text: formatMessage({ id: 'customer.support.chat.input.file.size' }),
          });
          return;
        }
      }

      const { images, others } = classifyFiles(files);
      const imageUrls = await Promise.all(images.map(image => startUpload(image)));
      const otherUrls = await Promise.all(others.map(other => startUpload(other)));
      const commonData = {
        fieldId: defaultField._id,
        domain: DOMAIN,
        type: TYPE,
      };
      if (imageUrls.length === 0 && otherUrls.length === 0) {
        return;
      }
      if (imageUrls.length > 0) {
        const result = await createCustomerSupportChat({
          comment: {
            ...commonData,
            images: imageUrls,
          },
        });
        if (result.err) {
          showErrorPopup({ text: result.err.response.data.message });
        }
      }
      if (otherUrls.length > 0) {
        const result = await createCustomerSupportChat({
          comment: {
            ...commonData,
            attachments: otherUrls,
          },
        });
        if (result.err) {
          showErrorPopup({ text: result.err.response.data.message });
        }
      }
      refresh();
    } catch (error) {
      console.log(error);
    }
  };

  const handleTextareaChange = e => {
    const textarea = e.target;
    textarea.style.height = '48px';
    textarea.style.height = textarea.scrollHeight + 'px';
    setTextareaHeight(textarea.scrollHeight + 'px');
    setText(e.target.value);
  };

  const sendText = async () => {
    if (!text || isLoading) return;
    try {
      setIsLoading(true);
      const data = {
        comment: {
          text,
          fieldId: defaultField._id,
          domain: DOMAIN,
          type: TYPE,
        },
      };
      const res = await createCustomerSupportChat(data);
      if (res.err) {
        showErrorPopup({ text: res.err.response.data.message });
        return;
      }
      setText('');
      refresh();
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const onKeyPress = e => {
    if (e.nativeEvent.keyCode === 13) {
      if (!e.shiftKey) {
        e.preventDefault();
        sendText();
      }
    }
  };

  return (
    <div className='customer-support-chat-input-area'>
      <textarea
        autoFocus
        className='customer-support-chat-input-area-textarea'
        placeholder={formatMessage({ id: 'customer.support.chat.input.placeholder' })}
        value={text}
        onChange={handleTextareaChange}
        ref={inputRef}
        style={{ height: textareaHeight }}
        onKeyPress={onKeyPress}
      />
      <div className='customer-support-chat-input-area-button-area'>
        <div
          className='customer-support-chat-input-area-button-area-file cursor-pointer no-drag'
          onClick={() => {
            inputFileRef.current.click();
          }}
        >
          <img src={icAttachment} alt='attachment' style={{ width: '20px', height: '20px' }} />
          <input type='file' ref={inputFileRef} onChange={e => sendFiles({ files: e.target.files })} style={{ display: 'none' }} multiple />
        </div>
        <BasicButton type='primary' textId='customer.support.chat.input.button' size='small' onClick={sendText} />
      </div>
    </div>
  );
};

export default InputArea;
